<template>
  <v-app :dark="isDarkMode">
    <!-- Loading overlay -->
    <div class="loading-overlay" v-if="isLoading">
      <div class="loader">
        <div class="circle"></div>
        <div class="circle"></div>
        <div class="circle"></div>
        <div class="circle"></div>
      </div>
      <div class="loading-text">Loading Portfolio...</div>
    </div>

    <!-- Main content -->
    <transition :name="transitionName" mode="out-in" appear>
      <router-view class="global-router" v-if="!isLoading"></router-view>
    </transition>

    <!-- Theme toggle button -->
    <v-btn fab class="theme-toggle-btn" @click="toggleDarkMode" :color="isDarkMode ? 'primary' : 'grey lighten-3'"
      elevation="4">
      <v-icon>{{ isDarkMode ? 'mdi-weather-sunny' : 'mdi-weather-night' }}</v-icon>
      <span class="theme-toggle-tooltip">{{ isDarkMode ? 'Light Mode' : 'Dark Mode' }}</span>
    </v-btn>

    <snackbar ref="comsnackbar"></snackbar>
    <fab-button :bottom="fabBottom" :right="fabRight" v-if="fabPosition && !isLoading" />
  </v-app>
</template>

<script>
import Snackbar from "@/components/Snackbar.vue";
import FabButton from "@/components/FabBtn.vue";
import { mapActions, mapMutations, mapGetters } from "vuex";

import portfolioImage from "@/assets/cboy2.png";

export default {
  name: "App",
  metaInfo() {
    return {
      title: "Thomas Antwi | Software Engineer",
      meta: [
        { property: "og:title", content: "Thomas Antwi | Software Engineer" },
        {
          property: "og:description",
          content:
            "Welcome to my portfolio! I am a dedicated Software Engineer with expertise in Vue.js, web development, and building dynamic, responsive user experiences. Explore my projects to see how I transform ideas into efficient, scalable web applications and solutions.",
        },
        { property: "og:image", content: portfolioImage },
        { property: "og:url", content: `${window.location.origin}${this.$route.path}` },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: "Thomas Antwi Portfolio" },
      ],
    };
  },
  components: { Snackbar, FabButton },
  data() {
    return {
      transitionName: "fade",
      isLoading: true,
      isDarkMode: false,
    };
  },
  computed: {
    ...mapGetters([
      "fabPosition",
      "fabVisibility"
    ]),
    fabBottom() {
      return this.fabPosition ? this.fabPosition.bottom : "25px";
    },
    fabRight() {
      return this.fabPosition ? this.fabPosition.right : "16px";
    },
  },
  created() {
    // Check for saved theme preference
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme) {
      this.isDarkMode = savedTheme === 'true';
      // Ensure Vuetify theme is synchronized
      this.$vuetify.theme.dark = this.isDarkMode;
    } else {
      // If no saved preference, initialize localStorage with current state
      localStorage.setItem('darkMode', this.isDarkMode);
    }

    // Apply theme class to document body for global CSS targeting
    if (this.isDarkMode) {
      document.body.classList.add('theme--dark');
    } else {
      document.body.classList.remove('theme--dark');
    }
  },
  mounted() {
    // Call the action to set global settings
    this.initializeSettings();
    this.updateFabSettings();

    // Set document title and favicon
    document.title = "Thomas Antwi | Software Engineer";
    let link = document.querySelector("#dynamic-favicon");
    link.href = this.$store.getters.settingGlobal.logo;

    // Simulate loading time (remove in production if not needed)
    setTimeout(() => {
      this.isLoading = false;
    }, 1500);
  },
  methods: {
    ...mapMutations([
      "setFabPosition",
      "setSnackbar",
    ]),
    ...mapActions([
      "initializeSettings",
    ]),

    toggleDarkMode() {
      this.isDarkMode = !this.isDarkMode;
      this.$vuetify.theme.dark = this.isDarkMode;
      localStorage.setItem('darkMode', this.isDarkMode);

      // Update body class for global CSS targeting
      if (this.isDarkMode) {
        document.body.classList.add('theme--dark');
      } else {
        document.body.classList.remove('theme--dark');
      }
    },

    updateFabSettings() {
      this.setFabPosition({ bottom: "65px", right: "16px" });
    },
  },
};
</script>

<style lang="scss">
@import "@/styles/variables.scss";

// Page transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

// Theme toggle button
.theme-toggle-btn {
  position: fixed !important;
  top: 50px;
  right: 20px;
  z-index: 999;
  box-shadow: $shadow-medium;
  transition: all $transition-medium;
  overflow: visible;

  &:hover {
    transform: rotate(30deg);

    .theme-toggle-tooltip {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  .theme-toggle-tooltip {
    position: absolute;
    bottom: -40px;
    left: 50%;
    transform: translateX(-50%) translateY(10px);
    background-color: rgba(0, 0, 0, 0.7);
    color: white;
    padding: 5px 10px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    opacity: 0;
    transition: all 0.3s ease;
    pointer-events: none;

    &::before {
      content: '';
      position: absolute;
      top: -5px;
      left: 50%;
      transform: translateX(-50%);
      border-width: 0 5px 5px 5px;
      border-style: solid;
      border-color: transparent transparent rgba(0, 0, 0, 0.7) transparent;
    }
  }

  @media (max-width: 600px) {
    top: 10px;
    right: 10px;
    transform: scale(0.9);

    &:hover {
      transform: scale(0.9) rotate(30deg);
    }
  }
}

// Loading animation
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--v-background-base);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-text {
  margin-top: 20px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 1.2rem;
  color: var(--v-primary-base);
  letter-spacing: 1px;
}

.loader {
  display: flex;
  justify-content: center;
  align-items: center;
}

.circle {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  margin: 0 8px;
  background-color: var(--v-primary-base);
  animation: bounce 1.5s infinite ease-in-out;
}

.circle:nth-child(1) {
  animation-delay: -0.3s;
}

.circle:nth-child(2) {
  animation-delay: -0.15s;
}

.circle:nth-child(3) {
  animation-delay: 0s;
}

.circle:nth-child(4) {
  animation-delay: 0.15s;
}

@keyframes bounce {

  0%,
  80%,
  100% {
    transform: scale(0);
  }

  40% {
    transform: scale(1);
  }
}
</style>
